import React, {useEffect, useState} from 'react';
import {Input, Button, DatePicker, message, Modal, Badge, Space, Switch, Spin} from 'antd';
import {ArrowLeft, Building2, CalendarClock, Hash, Image, KeyRound, Mail, MapPin, MessageSquare, Phone, Save, UserRound, X} from 'lucide-react';
import {DesktopOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import * as moment from "dayjs";
import {getSmsClockBadgeColor, getStoredUser, saveStoredUser} from "../../utils";
import {APIv1} from "../../api";
import ShowUserPicture from "../../components/UserProfileCom/ShowUserPicture/ShowUserPicture";
import {Device} from "../index";
import PaymentProvidersPermissionCheckBox
    from "../../components/UserProfileCom/PaymentProvidersPermissions/PaymentProvidersPermission";
import {updateUserData} from "../../components/UserProfileCom/updateUserData";
import {useTranslation} from "react-i18next";
import {updateUserPassword} from "../../api/user";
import ThemeS2P from "../../components/UserProfileCom/Scan2Pay/ThemeS2P/ThemeS2P";
import TipS2P from "../../components/UserProfileCom/Scan2Pay/TipS2P/TipS2P";
import OrderHistoryRange from "../../components/UserProfileCom/OrderHistoryRange/OrderHistoryRange";
import ProfileCompanySwitcher from "../../components/UserProfileCom/ProfileCompanySwitcher/ProfileCompanySwitcher";
import ProfileHeroSlot from "../../components/UserProfileCom/ProfileHeroSlot/ProfileHeroSlot";
import './UserProfile.scss';
import {playFeedbackWave} from "../../utils/feedbackWave";

const {RangePicker} = DatePicker;

const dayjs = require('dayjs')
const utc = require('dayjs/plugin/utc')

dayjs.extend(utc)

const getProviderPermissionState = (data) => ({
    payme: data.payme,
    click: data.click,
    uzum: data.uzum,
    anor: data.anor,
});

const normalizeProfileData = (data) => ({
    key: data.id,
    companyName: data.name,
    inn: data.inn,
    username: data.user?.username,
    password: data.public_password ? atob(data.public_password) : '',
    email: data.user?.email,
    phone: data.phone_number,
    address: data.address,
    isSendSms: data.send_sms,
    orderHistoryRange: data.order_history_range,
    totalSms: data.count_sent_sms,
    successSms: data.count_sent_sms - Math.floor(data.count_sent_sms * 0.9),
    errorSms: data.count_sent_sms - Math.floor(data.count_sent_sms * 0.1),
    billingLogo: data.billing_logo,
    logo: data.logo,
    banner: data.banner,
    s2pTheme: data.scan2pay_theme,
    isS2pTip: data.is_scan2pay_tip,
    s2pDefaultTipPercent: data.scan2pay_default_tip_percent,
    payme: data.pay_me,
    click: data.click,
    uzum: data.apelsin,
    anor: data.anor,
    startDate: moment.utc(data.start_date),
    endDate: moment.utc(data.end_date),
});

const extractCompanies = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.results)) {
        return payload.results;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.companies)) {
        return payload.companies;
    }
    return payload ? [payload] : [];
};

const normalizeCompanyOption = (company) => ({
    key: company.id,
    inn: company.inn,
    companyName: company.company_name,
});

const UserProfile = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState([]);
    const [initialData, setInitialData] = useState([]);
    const [userData, setUserData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSmsShow, setIsSmsShow] = useState(true);
    const [providerPermissions, setProviderPermissions] = useState({});
    const [isProfileLoading, setIsProfileLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyInn, setSelectedCompanyInn] = useState(null);

    const applyProfileData = (data) => {
        setProfileData(data);
        setInitialData(data);
        setIsSmsShow(data.isSendSms);
        setProviderPermissions(getProviderPermissionState(data));
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!userData.token) {
                return;
            }

            setIsProfileLoading(true);

            try {
                const headers = {
                    Authorization: `Token ${userData.token}`,
                };

                if (selectedCompanyInn) {
                    headers['X-User-Company-INN'] = selectedCompanyInn;
                }

                const response = await APIv1.get('/company/profile/', {
                    headers
                });

                const data = normalizeProfileData(response.data);
                applyProfileData(data);
            } catch (err) {
                console.error('Something went wrong', err);
            } finally {
                setIsProfileLoading(false);
            }
        };

        fetchData();
    }, [userData.token, selectedCompanyInn]);

    useEffect(() => {
        const fetchCompanies = async () => {
            if (!userData.token) {
                return;
            }

            try {
                const companiesResponse = await APIv1.get('/company/profile/my-companies/', {
                    headers: {
                        Authorization: `Token ${userData.token}`
                    }
                });

                const normalizedCompanies = extractCompanies(companiesResponse.data)
                    .map(normalizeCompanyOption);

                setCompanies(normalizedCompanies);

                if (!selectedCompanyInn && normalizedCompanies.length) {
                    const nextCompanyInn = userData.company_inn || normalizedCompanies[0].inn;

                    if (!userData.company_inn) {
                        const storedUser = getStoredUser();
                        saveStoredUser({
                            ...storedUser,
                            company_inn: nextCompanyInn,
                        });
                        setUserData(prevData => ({
                            ...prevData,
                            company_inn: nextCompanyInn,
                        }));
                    }

                    setSelectedCompanyInn(nextCompanyInn);
                }
            } catch (err) {
                console.error('Failed to fetch user companies', err);
                setCompanies([]);
            }
        };

        fetchCompanies();
    }, [userData.token, userData.company_inn, selectedCompanyInn]);

    useEffect(() => {
        const items = getStoredUser();
        if (items) {
            setUserData(items);
            if (items.company_inn) {
                setSelectedCompanyInn(items.company_inn);
            }
        } else {
            setIsProfileLoading(false);
        }
    }, []);

    useEffect(() => {
        // Check for changes whenever profileData is updated
        const hasChanges = Object.keys(profileData).some(key => {
            if (dayjs.isDayjs(profileData[key])) {
                return !profileData[key].isSame(initialData[key]);
            }
            return profileData[key] !== initialData[key];
        });
        setHasChanges(hasChanges);
    }, [profileData]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setProfileData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSwitchChange = (checked, name) => { // Added for switch
        setProfileData(prevData => ({
            ...prevData,
            [name]: checked
        }));
    };

    const handleSave = async (profileData, companyInn, token, setInitialData, setHasChanges) => {
        companyInn = profileData.inn
        const isUpdateSuccessful = await updateUserData({data: profileData, companyInn, token});

        if (isUpdateSuccessful) {
            setInitialData(profileData);
            setHasChanges(false);
            playFeedbackWave('success');
            message.success(t("pages.user.profile.messages.success1"));
        } else {
            playFeedbackWave('failure');
            message.error(t("pages.user.profile.messages.error1"));
        }
    };

    const handleCancel = () => {
        setProfileData(initialData);
        setIsSmsShow(initialData.isSendSms);
        setProviderPermissions(getProviderPermissionState(initialData));
        setHasChanges(false);
        message.info(t("pages.user.profile.messages.info1"));
    };

    const showChangePasswordModal = () => {
        setIsChangePasswordModalVisible(true);
    };

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            message.error("Passwords do not match");
            return;
        }

        if (!newPassword || newPassword.length < 6) {
            message.error("Password must be at least 6 characters long");
            return;
        }

        try {
            const isUpdated = await updateUserPassword({
                password: newPassword,
                token: userData.token,
            });

            if (isUpdated) {
                playFeedbackWave('success');
                message.success(t("pages.user.profile.messages.success2"));
                return;
            } else {
                playFeedbackWave('failure');
                message.error("Failed to update password");
            }
        } catch (err) {
            console.error('Error updating password:', err);
            playFeedbackWave('failure');
            message.error('An error occurred. Please try again later.');
        } finally {
            setIsChangePasswordModalVisible(false);
        }

        setNewPassword('');
        setConfirmPassword('');
    };

    const handleProviderPermissionChange = (newCheckedList) => {
        const nextPermissions = {
            payme: newCheckedList.includes('payme'),
            click: newCheckedList.includes('click'),
            uzum: newCheckedList.includes('uzum'),
            anor: newCheckedList.includes('anor'),
        };

        setProviderPermissions(nextPermissions);
        setProfileData(prevData => ({
            ...prevData,
            ...nextPermissions,
        }));
    };

    const handleS2PTipsChange = (newTipData) => {
        setProfileData((prevData) => ({
            ...prevData,
            isS2pTip: newTipData.isS2pTip,
            s2pDefaultTipPercent: newTipData.s2pDefaultTipPercent,
        }))
    }

    const handleCompanySelect = (company) => {
        const storedUser = getStoredUser();
        const nextUser = {
            ...storedUser,
            company_inn: company.inn,
        };

        saveStoredUser(nextUser);
        setUserData(prevData => ({
            ...prevData,
            company_inn: company.inn,
        }));
        setSelectedCompanyInn(company.inn);
    };

    return (
        <section className="user-profile">
            {isProfileLoading ? (
                <div className="profile-loading">
                    <Spin size="large"/>
                </div>
            ) : (
            <>
            <div className="profile-shell">
                <ProfileHeroSlot>
                    <div className="profile-hero__main">
                        <Button
                            icon={<ArrowLeft size={17}/>}
                            type="text"
                            className="profile-back-button"
                            onClick={() => navigate(-1)}
                        />
                        <div className="profile-hero__copy">
                            <h1 className='profile-title'>{profileData.companyName || t("pages.user.profile.title")}</h1>
                            <div className="profile-hero__meta">
                                <span><UserRound size={15}/>{profileData.username || '-'}</span>
                                <span><Building2 size={15}/>{profileData.companyName || '-'}</span>
                                <span><Hash size={15}/>{profileData.inn || '-'}</span>
                            </div>
                            <div className="profile-actions profile-actions--mobile">
                                <Button
                                    icon={<Save size={16}/>}
                                    type="primary"
                                    className={`save-button ${hasChanges ? '' : 'no-changes'}`}
                                    onClick={() =>
                                        handleSave(
                                            profileData,
                                            profileData.inn,
                                            userData.token,
                                            setInitialData,
                                            setHasChanges
                                        )}
                                    disabled={!hasChanges}
                                >
                                    {t("pages.user.profile.buttons.button1")}
                                </Button>
                                <Button
                                    icon={<X size={16}/>}
                                    type="text"
                                    className="cancel-button"
                                    onClick={handleCancel}
                                    disabled={!hasChanges}
                                >
                                    {t("pages.user.profile.buttons.button2")}
                                </Button>
                            </div>
                        </div>
                        <ProfileCompanySwitcher
                            companies={companies}
                            selectedCompanyInn={selectedCompanyInn}
                            onSelect={handleCompanySelect}
                        />
                    </div>
                    <div className="profile-actions profile-actions--desktop">
                        <Button
                            icon={<Save size={16}/>}
                            type="primary"
                            className={`save-button ${hasChanges ? '' : 'no-changes'}`}
                            onClick={() =>
                                handleSave(
                                    profileData,
                                    profileData.inn,
                                    userData.token,
                                    setInitialData,
                                    setHasChanges
                                )}
                            disabled={!hasChanges}
                        >
                            {t("pages.user.profile.buttons.button1")}
                        </Button>
                        <Button
                            icon={<X size={16}/>}
                            type="text"
                            className="cancel-button"
                            onClick={handleCancel}
                            disabled={!hasChanges}
                        >
                            {t("pages.user.profile.buttons.button2")}
                        </Button>
                    </div>
                </ProfileHeroSlot>

                <div className="profile-container">
                    <div className="profile-form">
                        <section className="profile-panel profile-panel--company">
                            <div className="profile-panel__header">
                                <span className="profile-panel__icon"><Building2 size={18}/></span>
                                <h2>{t("pages.user.profile.label1")}</h2>
                            </div>

                            <div className="form-grid">
                                <div className="form-group form-group--wide">
                                    <label htmlFor="companyName">{t("pages.user.profile.label1")}</label>
                                    <Input
                                        id="companyName"
                                        name="companyName"
                                        value={profileData.companyName}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="inn">{t("common.inn")}</label>
                                    <Input
                                        id="inn"
                                        name="inn"
                                        value={profileData.inn}
                                        onChange={handleInputChange}
                                        disabled={true}
                                    />
                                </div>
                                <div className="form-group form-group--wide">
                                    <label htmlFor="address"><MapPin size={14}/>{t("pages.user.profile.label6")}</label>
                                    <Input
                                        id="address"
                                        name="address"
                                        value={profileData.address}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group form-group--wide">
                                    <label><CalendarClock size={14}/>{t("pages.user.profile.label7")}</label>
                                    <RangePicker
                                        picker="date"
                                        id={{
                                            start: 'startInput',
                                            end: 'endInput',
                                        }}
                                        value={[profileData.startDate, profileData.endDate]}
                                        disabled={true}
                                        showTime
                                        format='DD-MM-YYYY   HH:mm:ss'
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="profile-panel profile-panel--account">
                            <div className="profile-panel__header">
                                <span className="profile-panel__icon"><UserRound size={18}/></span>
                                <h2>{t("pages.user.profile.label3")}</h2>
                            </div>

                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor="username"><UserRound size={14}/>{t("pages.user.profile.label3")}</label>
                                    <Input
                                        id="username"
                                        name="username"
                                        type="username"
                                        value={profileData.username}
                                        onChange={handleInputChange}
                                        disabled={true}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email"><Mail size={14}/>{t("pages.user.profile.label4")}</label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={profileData.email}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone"><Phone size={14}/>{t("pages.user.profile.label5")}</label>
                                    <Input
                                        id="phone"
                                        name="phone"
                                        value={profileData.phone}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group password-group">
                                    <label htmlFor="password"><KeyRound size={14}/>{t("pages.user.profile.label2")} <span
                                        className="password__warning">{t("pages.user.profile.messages.warning1")}</span></label>
                                    <div className="password-input">
                                        <Input.Password
                                            id="password"
                                            name="password"
                                            placeholder={t("pages.user.profile.label2")}
                                            value={profileData.password}
                                            onChange={handleInputChange}
                                            disabled={false}
                                            visibilityToggle={true}
                                        />
                                        <Button className="change-password-btn" onClick={showChangePasswordModal}>
                                            {t("pages.user.profile.buttons.button3")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="profile-panel profile-panel--media">
                            <div className="profile-panel__header profile-panel__header--split">
                                <div>
                                    <span className="profile-panel__icon"><Image size={18}/></span>
                                    <h2>{t("pages.user.profile.label11")}</h2>
                                </div>
                            </div>
                            <div className="profile-picture profile-picture--wide">
                                <ShowUserPicture
                                    data={{
                                        billingLogo: profileData.billingLogo,
                                        logo: profileData.logo,
                                        banner: profileData.banner,
                                    }}
                                    inn={profileData.inn}
                                    userToken={userData.token}
                                    uploadPlacement="card"
                                />
                            </div>
                        </section>

                        <section className="profile-panel profile-panel--communication">
                            <div className="profile-panel__header">
                                <span className="profile-panel__icon"><MessageSquare size={18}/></span>
                                <h2>{t("pages.user.profile.label15")}</h2>
                            </div>

                            <div className="profile-settings-grid">
                                <div className="profile-settings-row">
                                    <div className="form-group sms-control">
                                        <label htmlFor="sms">{t("pages.user.profile.label8")}</label>
                                        <Space className="sms-control__content">
                                            <Switch
                                                value={isSmsShow}
                                                onChange={(checked) => {
                                                    setIsSmsShow(checked);
                                                    handleSwitchChange(checked, 'isSendSms');
                                                }}
                                                checkedChildren={t("common.onStatus")} unCheckedChildren={t("common.offStatus")}
                                                style={{backgroundColor: isSmsShow ? "var(--color-status-on)" : "var(--color-status-off"}}/>
                                            <Badge count={profileData.totalSms} color={isSmsShow ? "#faad14" : "gray"}
                                                   overflowCount={Infinity}/>
                                            <Badge count={profileData.successSms} color={isSmsShow ? "#52c41a" : "gray"}
                                                   overflowCount={Infinity}/>
                                            <Badge count={getSmsClockBadgeColor(isSmsShow, 'clock')}
                                                   overflowCount={Infinity}/>
                                            <Badge
                                                className="site-badge-count-109"
                                                count={profileData.errorSms}
                                                color={isSmsShow ? "#f5222d" : "gray"}
                                                overflowCount={Infinity}
                                            />
                                        </Space>
                                    </div>

                                    <div className="form-group order-history-range">
                                        <label>{t("pages.user.profile.label16")}</label>
                                        <OrderHistoryRange value={profileData.orderHistoryRange}/>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <TipS2P
                                        t={t}
                                        isS2pTip={profileData.isS2pTip}
                                        s2pDefaultTipPercent={profileData.s2pDefaultTipPercent}
                                        onChange={handleS2PTipsChange}
                                    />
                                </div>

                                <div className="form-group-providers">
                                    <PaymentProvidersPermissionCheckBox
                                        t={t}
                                        providerPermissions={providerPermissions}
                                        onChange={handleProviderPermissionChange}
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="profile-panel profile-panel--theme">
                            <div className="profile-panel__header">
                                <span className="profile-panel__icon"><Image size={18}/></span>
                                <h2>{t("pages.user.profile.label13")}</h2>
                            </div>
                            <ThemeS2P
                                t={t}
                                currentTheme={profileData.s2pTheme || 'light'}
                                onThemeChange={(key) =>
                                    handleInputChange({target: {name: 's2pTheme', value: key}})
                                }
                            />
                        </section>
                    </div>

                    <section className="profile-panel profile-panel--devices">
                        <div className="profile-panel__header">
                            <span className="profile-panel__icon"><DesktopOutlined size={18}/></span>
                            <h2 className="related-device-title">{t("pages.user.profile.section1.title")}</h2>
                        </div>
                        <Device companyInn={profileData.inn}/>
                    </section>
                </div>
            </div>

            <Modal
                title="Change Password"
                open={isChangePasswordModalVisible}
                onOk={handleChangePassword}
                onCancel={() => setIsChangePasswordModalVisible(false)}
            >
                <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <Input.Password
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <Input.Password
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </div>
            </Modal>
            </>
            )}
        </section>
    );
};

export default UserProfile;
